import React, { useState, createRef } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  HStack,
  Tooltip,
  keyframes,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { InfoIcon, TimeIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import FullCalendar from "@fullcalendar/react";
import { EventInput, DateSelectArg, EventContentArg } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useSearchParams, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ContextDebugger } from "../../components/ContextDebugger";

const FIND_ANY_AVAILABILITIES = gql`
  query FindAnyAvailabilities($practitionerId: ID!, $startDate: String!, $endDate: String!) {
    getAvailabilityByPractitionerId(
      practitionerId: $practitionerId,
      startDate: $startDate,
      endDate: $endDate
    ) {
      id
      practitioner {
        id
      }
      startTime
      endTime
      isAvailable
      status
    }
  }
`;

const GET_PRACTITIONER_AVAILABILITY = gql`
  query GetAvailabilityByPractitionerId($practitionerId: ID!, $startDate: String!, $endDate: String!) {
    getAvailabilityByPractitionerId(
      practitionerId: $practitionerId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      practitioner {
        id
      }
      startTime
      endTime
      isAvailable
      status
    }
  }
`;

const CREATE_AVAILABILITY = gql`
  mutation CreateAvailability($input: AvailabilityInput!) {
    createAvailability(input: $input) {
      id
      startTime
      endTime
      status
    }
  }
`;

const DELETE_AVAILABILITY = gql`
  mutation DeleteAvailability($id: ID!) {
    deleteAvailability(id: $id)
  }
`;

// Add shimmer animation for better UI feedback
const shimmerAnimation = keyframes`
  0% { background-position: -500px 0; }
  100% { background-position: 500px 0; }
`;

// Add glow animation
const glowAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(57, 161, 105, 0.5); }
  50% { box-shadow: 0 0 20px rgba(57, 161, 105, 0.8); }
`;

export const SetAvailabilities = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<{ id: string; title: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Add state for 24-hour view toggle
  const [is24HourView, setIs24HourView] = useState(false);

  // Get practitionerId from URL params, similar to ClientBookingCalendarByPractitionerId
  const { practitionerId } = useParams<{ practitionerId: string }>();
  
  // Use practitionerId from URL if available, otherwise fall back to user's ID
  const effectivePractitionerId = practitionerId || user?.id || "";

  // Log the practitioner ID when component loads with more details
  React.useEffect(() => {
    console.log("Using practitioner ID for availability:", {
      effectivePractitionerId,
      fromURL: practitionerId,
      fromUser: user?.id,
      userInfo: user
    });
    
    console.log("📌 IMPORTANT DEBUG: For URL practitioner vs. user ID:", {
      urlPractitionerId: practitionerId, 
      userId: user?.id,
      match: practitionerId === user?.id,
      finalIdUsed: practitionerId || user?.id
    });
    
    // Add a direct comparison of the IDs to help debug
    if (user?.id && user.id !== effectivePractitionerId) {
      console.warn("⚠️ ID MISMATCH: User ID and effective practitioner ID don't match!", {
        userId: user.id,
        effectivePractitionerId
      });
    }
  }, [effectivePractitionerId, practitionerId, user]);

  // Calculate date range for query based on calendar view
  const [queryDateRange, setQueryDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 21)).toISOString()
  });

  const [createAvailability, { loading: createLoading }] = useMutation(CREATE_AVAILABILITY);
  const [deleteAvailability, { loading: deleteLoading }] = useMutation(DELETE_AVAILABILITY);

  const { data, loading, error, refetch } = useQuery(GET_PRACTITIONER_AVAILABILITY, {
    variables: {
      practitionerId: practitionerId || effectivePractitionerId,
      startDate: queryDateRange.startDate,
      endDate: queryDateRange.endDate
    },
    skip: !effectivePractitionerId,
    onCompleted: (data) => {
      console.log("Availability data received:", {
        data,
        practitionerId: effectivePractitionerId,
        foundCount: data?.getAvailabilityByPractitionerId?.length || 0,
        dateRange: queryDateRange
      });
    },
    onError: (error) => {
      console.error("Error fetching availability:", {
        error,
        practitionerId: effectivePractitionerId, 
        dateRange: queryDateRange
      });
      toast({
        title: "Error",
        description: "Failed to fetch your availability. Please try again.",
        status: "error",
        duration: 3000
      });
    }
  });

  // Add debug query to check both the user ID and the hardcoded ID to see which one works
  const { data: anyAvailabilityData } = useQuery(FIND_ANY_AVAILABILITIES, {
    variables: {
      practitionerId: practitionerId || effectivePractitionerId,
      startDate: queryDateRange.startDate,
      endDate: queryDateRange.endDate
    },
    onCompleted: (data) => {
      console.log("DEBUG - Testing for availabilities with current ID:", {
        data,
        count: data?.getAvailabilityByPractitionerId?.length || 0,
        practitionerId: effectivePractitionerId,
        availabilities: data?.getAvailabilityByPractitionerId
      });
    },
    onError: (error) => {
      console.error("Error checking availabilities:", error);
    }
  });

  // Store the events from our debug query
  const additionalEvents = React.useMemo(() => {
    if (!anyAvailabilityData?.getAvailabilityByPractitionerId) return [];
    
    return anyAvailabilityData.getAvailabilityByPractitionerId.map((slot: any) => ({
      id: slot.id,
      start: slot.startTime,
      end: slot.endTime,
      title: `Available (${slot.status})`,
      backgroundColor: "#38A169", // Green color
      borderColor: "#2F855A",
      textColor: "#ffffff",
      editable: false,
      allDay: false
    }));
  }, [anyAvailabilityData]);

  const handleDatesSet = (dateInfo: any) => {
    // Get the start of the week being displayed
    const weekStart = dateInfo.start;
    
    // Update URL with the week's start date
    setSearchParams({ 
      week: weekStart.toISOString(),
      view: "timeGridWeek" // Add view to URL params
    });
    
    // Update query date range based on calendar view
    setQueryDateRange({
      startDate: dateInfo.start.toISOString(),
      endDate: dateInfo.end.toISOString()
    });
    
    console.log("Calendar dates changed:", {
      weekStart: weekStart.toISOString(),
      weekEnd: dateInfo.end.toISOString(),
      displayRange: `${weekStart.toLocaleDateString()} - ${dateInfo.end.toLocaleDateString()}`
    });
  };

  // Improved initialDate handling to ensure consistent format
  const initialDate = React.useMemo(() => {
    const weekParam = searchParams.get("week");
    
    try {
      if (weekParam) {
        // Ensure date is properly parsed by creating a new Date object
        const date = new Date(weekParam);
        
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          console.log("Using date from URL:", date.toISOString());
          return date;
        }
      }
    } catch (error) {
      console.error("Error parsing date from URL:", error);
    }

    // Default fallback - current week
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setHours(0, 0, 0, 0);
    currentWeekStart.setDate(today.getDate() - today.getDay()); // Set to Sunday
    
    // Update URL with current week
    setSearchParams({ week: currentWeekStart.toISOString(), view: "timeGridWeek" });
    
    return currentWeekStart;
  }, [searchParams, setSearchParams]);

  const handleSelect = (selectInfo: DateSelectArg) => {
    setSelectedSlot({
      start: selectInfo.start,
      end: selectInfo.end
    });
    onOpen();
  };

  const handleSaveAvailability = async () => {
    if (!selectedSlot) return;
    
    try {
      // IMPORTANT: Always prioritize the practitionerId from the URL
      // This is likely what the user expects - they're setting availability for the ID in the URL
      const practitionerIdToUse = practitionerId || user?.id;
      
      console.log("Creating availability with details:", {
        urlPractitionerId: practitionerId,
        userId: user?.id,
        chosenId: practitionerIdToUse,
        startTime: selectedSlot.start.toISOString(),
        endTime: selectedSlot.end.toISOString()
      });
      
      console.log("⭐ IMPORTANT: Using URL practitioner ID for creation:", practitionerIdToUse);
      
      await createAvailability({
        variables: {
          input: {
            practitioner: practitionerIdToUse,
            startTime: selectedSlot.start.toISOString(),
            endTime: selectedSlot.end.toISOString(),
            isAvailable: true,
            status: "DRAFT"
          }
        }
      });

      // Update refetch to use the same ID we just used
      await refetch({
        practitionerId: practitionerIdToUse,
        startDate: queryDateRange.startDate,
        endDate: queryDateRange.endDate
      });
      
      onClose();
      
      toast({
        title: "Availability created",
        description: "The time slot has been added to your calendar",
        status: "success",
        duration: 3000
      });
    } catch (error) {
      console.error("Error creating availability:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create availability",
        status: "error",
        duration: 5000
      });
    }
  };

  const handleEventClick = (clickInfo: any) => {
    // Only allow deletion of availability events
    if (clickInfo.event.id) {
      setSelectedEvent({
        id: clickInfo.event.id,
        title: clickInfo.event.title
      });
      onDeleteModalOpen();
    }
  };

  const handleDeleteAvailability = async () => {
    console.log("Deleting availability:", selectedEvent);
    if (!selectedEvent) return;

    try {
      const result = await deleteAvailability({
        variables: {
          id: selectedEvent.id
        }
      });

      if (result.data.deleteAvailability) {
        await refetch();
        onDeleteModalClose();
        
        toast({
          title: "Availability deleted",
          description: "The time slot has been removed from your calendar",
          status: "success",
          duration: 3000
        });
      } else {
        throw new Error("Failed to delete availability");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete availability",
        status: "error",
        duration: 5000
      });
    }
  };

  // Combine fetched events
  const events: EventInput[] = React.useMemo(() => {
    // First try fetched events from the normal query
    const fetchedEvents = data?.getAvailabilityByPractitionerId?.map((slot: any) => {
      console.log("Processing availability slot:", slot);
      return {
        id: slot.id,
        start: slot.startTime,
        end: slot.endTime,
        title: `Available (${slot.status})`,
        backgroundColor: "#38A169", // Green color
        borderColor: "#2F855A",
        textColor: "#ffffff",
        editable: false,
        allDay: false
      };
    }) || [];

    // Combine events from both queries
    const combinedEvents = [...fetchedEvents, ...additionalEvents];
    
    // Remove duplicates by ID
    const uniqueEvents = combinedEvents.filter((event, index, self) =>
      index === self.findIndex((e) => e.id === event.id)
    );

    console.log("Calendar events:", {
      fetched: fetchedEvents.length,
      additional: additionalEvents.length,
      combined: uniqueEvents.length
    });

    return uniqueEvents;
  }, [data, additionalEvents]);

  // Add a ref to access the calendar API
  const calendarRef = createRef<any>();
  
  // Add these navigation handlers
  const handlePrevClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleNextClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
    }
  };

  // Handle the toggle for 24-hour view
  const toggle24HourView = () => {
    setIs24HourView(prev => !prev);
  };

  // Customized event rendering
  const renderEventContent = (eventInfo: EventContentArg) => {
    const isMobile = window.innerWidth < 768;
    
    return (
      <Box 
        fontSize={isMobile ? "xs" : "sm"} 
        overflow="hidden" 
        width="100%"
        height="100%"
        position="relative"
        borderRadius="md"
        cursor="pointer"
        transition="all 0.3s ease"
        _before={{
          content: "\"\"",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(120deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 70%)",
          backgroundSize: "1000px 100%",
          animation: `${shimmerAnimation} 3s infinite linear`,
          zIndex: 0
        }}
        _hover={{
          transform: "scale(1.03)",
          boxShadow: "0 0 15px rgba(57, 161, 105, 0.7)"
        }}
      >
        <Tooltip
          label={
            <VStack spacing={2} p={2} align="start">
              <Heading size="xs">{eventInfo.event.title}</Heading>
              <Text fontSize="xs" fontWeight="bold">
                {new Date(eventInfo.event.start!).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})} - {new Date(eventInfo.event.end!).toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
              </Text>
              <Text fontSize="xs">Click to remove this slot</Text>
            </VStack>
          }
          placement="top"
          hasArrow
          gutter={10}
          bg="green.600"
          borderRadius="md"
        >
          <Box p={1} position="relative" zIndex={1}>
            <HStack spacing={1} mb={1}>
              <Icon 
                as={TimeIcon} 
                color="yellow.400"
                boxSize={isMobile ? 2 : 3}
              />
              <Badge 
                colorScheme="green" 
                fontSize={isMobile ? "2xs" : "xs"}
                variant="solid"
              >
                Available
              </Badge>
            </HStack>
            
            <Text 
              fontWeight="bold" 
              fontSize={isMobile ? "2xs" : "xs"} 
              color="white"
            >
              {eventInfo.timeText}
            </Text>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  if (authLoading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <VStack spacing={4} align="center" justify="center" height="400px">
            <Text>Loading...</Text>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <VStack spacing={4} align="center" justify="center" height="400px">
            <Heading size="md">Please log in to set your availability</Heading>
            <Button colorScheme="blue" onClick={() => window.location.href = "/login"}>
              Log In
            </Button>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack spacing={{ base: 3, md: 4 }} align="stretch">
          <Box bg="blue.50" p={{ base: 3, md: 4 }} borderRadius="md">
            <Text fontSize={{ base: "sm", md: "md" }}>
              {practitionerId && practitionerId !== user?.id ? 
                "Setting availability for practitioner" : 
                `Welcome, ${user?.fName} ${user?.lName}! Set your availability by selecting time slots on the calendar below.`
              }
            </Text>
            
            {/* Display the practitioner ID prominently */}
            <HStack mt={2} justifyContent="space-between">
              <Text fontSize="sm" fontWeight="bold" color="blue.700">
                Using Practitioner ID: {practitionerId || user?.id}
              </Text>
              
              {/* Add warning if it doesn't match user ID */}
              {practitionerId && user?.id && practitionerId !== user?.id && (
                <Badge colorScheme="yellow" p={1} borderRadius="md">
                  <HStack>
                    <InfoIcon boxSize={3} />
                    <Text fontSize="xs">You're setting availability for another practitioner</Text>
                  </HStack>
                </Badge>
              )}
            </HStack>
          </Box>
          
          {/* Add a warning notice if we detect that saved availabilities might be for another ID */}
          {events.length === 0 && additionalEvents.length > 0 && (
            <Box bg="yellow.100" p={3} borderRadius="md" borderLeft="4px solid" borderColor="yellow.400">
              <Heading size="xs" mb={1}>No availabilities found for this practitioner</Heading>
              <Text fontSize="sm">
                Note: There are availabilities in the system, but they may be associated with a different practitioner ID.
              </Text>
            </Box>
          )}
          
          <Heading size={{ base: "md", md: "lg" }}>
            Set Your Availability {(loading) ? "(Loading...)" : ""}
          </Heading>
          
          {/* Add explanation section */}
          <HStack spacing={4} flexWrap="wrap" justify="space-between" pt={2} pb={4}>
            <Box 
              p={3} 
              bg="green.50" 
              borderRadius="md"
              borderLeft="4px solid" 
              borderColor="yellow.400"
              boxShadow="sm"
              _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
              transition="all 0.3s"
              animation={`${glowAnimation} 4s infinite ease-in-out`}
            >
              <Heading size="xs" mb={1} color="green.700">Quick Guide</Heading>
              <Text fontSize="xs" color="gray.600">Click and drag on the calendar to select time slots when you're available.</Text>
            </Box>

            <Button
              onClick={toggle24HourView}
              colorScheme={is24HourView ? "blue" : "gray"}
              size="sm"
              variant={is24HourView ? "solid" : "outline"}
              leftIcon={<Icon as={TimeIcon} />}
              transition="all 0.3s ease"
              boxShadow={is24HourView ? "md" : "none"}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md"
              }}
            >
              {is24HourView ? "Show Business Hours" : "Show Full 24 Hours"}
            </Button>
          </HStack>
        
          <Box 
            p={{ base: 2, md: 4 }} 
            borderWidth="1px" 
            borderRadius="lg"
            overflowX="auto"
            position="relative"
            boxShadow="lg"
            _before={{
              content: "\"\"",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "lg",
              padding: "2px",
              background: "linear-gradient(45deg, #3182CE, #38A169, #ECC94B)",
              mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              maskComposite: "xor",
              pointerEvents: "none"
            }}
          >
            {/* Add floating navigation buttons */}
            <Box
              position="absolute"
              left={{ base: 1, md: 2 }}
              top="50%"
              transform="translateY(-50%)"
              zIndex={10}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Button
                onClick={handlePrevClick}
                size="sm"
                borderRadius="full"
                width={{ base: "36px", md: "40px" }}
                height={{ base: "36px", md: "40px" }}
                p={0}
                bg="green.400"
                _hover={{ 
                  bg: "green.500", 
                  transform: "scale(1.1) translateX(-2px)", 
                  boxShadow: "0 0 15px rgba(57, 161, 105, 0.6)" 
                }}
                _active={{ bg: "green.600", transform: "scale(0.95)" }}
                transition="all 0.3s ease"
                boxShadow="0 2px 10px rgba(0,0,0,0.15)"
                aria-label="Previous"
              >
                <ChevronLeftIcon boxSize={{ base: 4, md: 6 }} color="white" />
              </Button>
            </Box>

            <Box
              position="absolute"
              right={{ base: 1, md: 2 }}
              top="50%"
              transform="translateY(-50%)"
              zIndex={10}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Button
                onClick={handleNextClick}
                size="sm"
                borderRadius="full"
                width={{ base: "36px", md: "40px" }}
                height={{ base: "36px", md: "40px" }}
                p={0}
                bg="green.400"
                _hover={{ 
                  bg: "green.500", 
                  transform: "scale(1.1) translateX(2px)", 
                  boxShadow: "0 0 15px rgba(57, 161, 105, 0.6)" 
                }}
                _active={{ bg: "green.600", transform: "scale(0.95)" }}
                transition="all 0.3s ease"
                boxShadow="0 2px 10px rgba(0,0,0,0.15)"
                aria-label="Next"
              >
                <ChevronRightIcon boxSize={{ base: 4, md: 6 }} color="white" />
              </Button>
            </Box>
            
            {/* Add CSS for fullcalendar custom styling */}
            <style>
              {`
                .fc-event {
                  cursor: pointer;
                  transition: all 0.2s ease;
                  overflow: hidden;
                }
                .fc-event:hover {
                  transform: scale(1.02);
                  box-shadow: 0 5px 15px rgba(0,0,0,0.2) !important;
                  z-index: 100 !important;
                }
                .fc-event-title {
                  font-weight: bold;
                }
                .fc-timegrid-event-harness {
                  z-index: 5;
                }
                ${is24HourView ? `
                /* 24-hour specific styles */
                .fc-timegrid-slot {
                  height: 25px !important;
                  min-height: 25px !important;
                }
                .fc-timegrid-slot-lane {
                  height: 25px !important;
                }
                .fc-timegrid-col.fc-day {
                  min-height: 600px !important;
                }
                /* Night hours styling (midnight to 8am and 10pm to midnight) */
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="00:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="01:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="02:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="03:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="04:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="05:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="06:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="07:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="22:"],
                .fc-timegrid-slot.fc-timegrid-slot-lane[data-time^="23:"] {
                  background-color: rgba(0, 0, 0, 0.03);
                }
                /* Current time indicator more visible */
                .fc-timegrid-now-indicator-line {
                  border-color: #E53E3E !important;
                  border-width: 2px !important;
                }
                .fc-timegrid-now-indicator-arrow {
                  border-color: #E53E3E !important;
                  border-width: 5px !important;
                }
                ` : ""}
                /* Business hours highlight */
                .fc-non-business {
                  background: rgba(0, 0, 0, 0.025);
                }
                /* Selection highlight */
                .fc-highlight {
                  background: rgba(56, 161, 105, 0.3) !important;
                }
                @media (max-width: 768px) {
                  .fc-event {
                    font-size: 0.7em;
                  }
                  /* Fix week view title on mobile */
                  .fc-timeGridWeek-view .fc-toolbar-title {
                    font-size: 0.9rem !important; 
                    white-space: nowrap !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                  }
                  /* Apply to all toolbar titles on very small screens */
                  @media (max-width: 480px) {
                    .fc-toolbar-title {
                      font-size: 0.85rem !important;
                      line-height: 1.2 !important;
                    }
                  }
                  ${is24HourView ? `
                  /* Mobile 24-hour view */
                  .fc-timegrid-slot {
                    height: 20px !important;
                    min-height: 20px !important;
                  }
                  ` : ""}
                }
              `}
            </style>
            
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
              initialView="timeGridWeek"
              initialDate={initialDate}
              datesSet={handleDatesSet}
              events={events}
              
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay"
              }}

              // Add business hours highlighting
              businessHours={{
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // All days
                startTime: "08:00",
                endTime: "22:00"
              }}
              
              // Set specific height when in 24-hour view to show all hours
              height={is24HourView ? "1400px" : "auto"}
              expandRows={true}
              stickyHeaderDates={true}
              
              // Better mobile settings for week view
              views={{
                timeGridDay: {
                  titleFormat: { month: "short", day: "numeric" },
                  dayHeaderFormat: { weekday: "long" },
                  // Keep 30min slots even in 24hr view for better detail
                  slotDuration: "00:30:00",
                  slotLabelInterval: "01:00:00"
                },
                timeGridWeek: {
                  titleFormat: { month: "short", year: "numeric" },
                  dayHeaderFormat: { weekday: "short", day: "numeric" },
                  // Use 1hr slots for consistency in both views
                  slotDuration: "01:00:00",
                  slotLabelInterval: "01:00:00"
                },
                dayGridMonth: {
                  dayHeaderFormat: { weekday: "narrow" }
                }
              }}
              
              // Customize slot label format
              slotLabelFormat={{
                hour: "numeric",
                minute: "2-digit",
                omitZeroMinute: true,
                meridiem: "short"
              }}
              
              slotMinTime={is24HourView ? "00:00:00" : "08:00:00"} // Toggle between full day and business hours
              slotMaxTime={is24HourView ? "24:00:00" : "22:00:00"} // Toggle between full day and business hours
              allDaySlot={false}
              nowIndicator={true}
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              selectable={true}
              select={handleSelect}
              
              // Improve display on small screens
              eventTimeFormat={{
                hour: "numeric",
                minute: "2-digit",
                meridiem: "short"
              }}
            />
          </Box>
          
          {/* Instructions section */}
          <Box mt={6} p={5} bg="blue.50" borderRadius="md" boxShadow="sm">
            <Heading size="sm" mb={3}>How to Set Your Availability</Heading>
            <VStack align="start" spacing={2}>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">1.</Box>
                <Text>Click and drag on the calendar to select time slots when you're available.</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">2.</Box>
                <Text>Confirm each time slot in the dialog that appears.</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">3.</Box>
                <Text>To remove a time slot, click on it and confirm deletion.</Text>
              </HStack>
              <HStack align="start">
                <Box as="span" fontWeight="bold" minWidth="30px">4.</Box>
                <Text>Green slots on the calendar show your current availability.</Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Create Availability Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "sm", md: "md" }}>
        <ModalOverlay 
          bg="rgba(0,0,0,0.7)"
          backdropFilter="blur(6px)"
        />
        <ModalContent 
          mx={{ base: 3, md: 0 }}
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 10px 30px -5px rgba(0, 0, 0, 0.3)"
          bg="white"
          position="relative"
          _before={{
            content: "\"\"",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #38A169, #48BB78)",
            zIndex: 0
          }}
        >
          <ModalHeader 
            fontSize={{ base: "lg", md: "xl" }}
            py={4}
            bg="green.50"
            color="green.700"
          >
            Create Availability
          </ModalHeader>
          <ModalBody fontSize={{ base: "sm", md: "md" }} py={6}>
            {selectedSlot && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="green.50" borderRadius="md">
                  <Text fontWeight="bold">Selected Time:</Text>
                  <Text>
                    Date: {selectedSlot.start.toLocaleDateString()}
                  </Text>
                  <Text>
                    From: {selectedSlot.start.toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                  </Text>
                  <Text>
                    To: {selectedSlot.end.toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"})}
                  </Text>
                </Box>
                
                <Text>
                  This will mark you as available during this time slot.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="green" 
              mr={3} 
              onClick={handleSaveAvailability}
              isLoading={createLoading}
              loadingText="Saving..."
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
              boxShadow="md"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
                bg: "green.500"
              }}
              transition="all 0.2s"
            >
              Save Availability
            </Button>
            <Button 
              variant="outline" 
              colorScheme="gray"
              onClick={onClose}
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Availability Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} size={{ base: "sm", md: "md" }}>
        <ModalOverlay 
          bg="rgba(0,0,0,0.7)"
          backdropFilter="blur(6px)"
        />
        <ModalContent 
          mx={{ base: 3, md: 0 }}
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 10px 30px -5px rgba(0, 0, 0, 0.3)"
          bg="white"
          position="relative"
          _before={{
            content: "\"\"",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #E53E3E, #F56565)",
            zIndex: 0
          }}
        >
          <ModalHeader 
            fontSize={{ base: "lg", md: "xl" }}
            py={4}
            bg="red.50"
            color="red.700"
          >
            Delete Availability
          </ModalHeader>
          <ModalBody fontSize={{ base: "sm", md: "md" }} py={6}>
            {selectedEvent && (
              <VStack spacing={4} align="stretch">
                <Text>
                  Are you sure you want to delete this availability slot?
                </Text>
                <Box p={4} bg="red.50" borderRadius="md">
                  <Text fontWeight="bold">
                    {selectedEvent.title}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="red" 
              mr={3} 
              onClick={handleDeleteAvailability}
              isLoading={deleteLoading}
              loadingText="Deleting..."
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
              boxShadow="md"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg"
              }}
              transition="all 0.2s"
            >
              Delete
            </Button>
            <Button 
              variant="outline" 
              colorScheme="gray"
              onClick={onDeleteModalClose}
              size={{ base: "sm", md: "md" }}
              flex={{ base: 1, md: "unset" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FooterWithFourColumns />
      <ContextDebugger />
    </>
  );
};
